import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  User, Package, Heart, MapPin, RotateCcw, Settings, LogOut,
  Crown, ChevronRight, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIP_TIERS = {
  silver: { label: 'Silver', color: 'bg-slate-200 text-slate-700', icon: '🥈' },
  gold: { label: 'Gold', color: 'bg-gold/20 text-gold', icon: '🥇' },
  platinum: { label: 'Platinum', color: 'bg-purple-100 text-purple-700', icon: '💎' }
};

export const AccountDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      toast.error('Please login to access your account');
      return;
    }
    loadProfile();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/account/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data.profile);
    } catch (error) {
      console.error('Error loading profile:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/');
        toast.error('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const navItems = [
    { path: '/account', icon: User, label: 'Overview', exact: true },
    { path: '/account/orders', icon: Package, label: 'My Orders' },
    { path: '/account/wishlist', icon: Heart, label: 'Wishlist' },
    { path: '/account/addresses', icon: MapPin, label: 'Addresses' },
    { path: '/account/returns', icon: RotateCcw, label: 'Returns' },
    { path: '/account/profile', icon: Settings, label: 'Profile Settings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const vipTier = profile?.vip_tier ? VIP_TIERS[profile.vip_tier] : null;

  return (
    <div className="min-h-screen bg-ivory" data-testid="account-dashboard">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Profile Card */}
            <div className="bg-white border border-charcoal/10 p-6 mb-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-gold mx-auto flex items-center justify-center mb-4">
                  <span className="text-3xl text-white font-heading">
                    {profile?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <h2 className="font-heading text-xl font-light text-charcoal">
                  {profile?.full_name || 'Customer'}
                </h2>
                <p className="text-sm text-charcoal-light">{profile?.email}</p>
                
                {/* VIP Badge */}
                {vipTier && (
                  <div className={`inline-flex items-center gap-2 px-3 py-1 mt-3 ${vipTier.color}`}>
                    <Crown className="w-4 h-4" />
                    <span className="text-sm font-medium">{vipTier.icon} {vipTier.label} Member</span>
                  </div>
                )}
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-charcoal/10">
                <div className="text-center">
                  <p className="font-heading text-2xl font-light text-gold">{profile?.total_orders || 0}</p>
                  <p className="text-xs text-charcoal-light">Orders</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-2xl font-light text-gold">{formatCurrency(profile?.total_spent)}</p>
                  <p className="text-xs text-charcoal-light">Total Spent</p>
                </div>
              </div>
              
              {/* Loyalty Points */}
              <div className="mt-4 p-3 bg-beige/50 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Star className="w-5 h-5 text-gold fill-gold" />
                  <span className="font-heading text-lg text-charcoal">{profile?.loyalty_points || 0}</span>
                </div>
                <p className="text-xs text-charcoal-light">Loyalty Points</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="bg-white border border-charcoal/10">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 border-b border-charcoal/5 last:border-b-0 transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-gold/5 text-gold'
                      : 'text-charcoal-light hover:bg-beige hover:text-charcoal'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm">{item.label}</span>
                  </div>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              ))}
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-between px-4 py-3 text-red-600 hover:bg-red-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <LogOut className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm">Logout</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Outlet context={{ profile, loadProfile }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;
